import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Search, Eye, Pencil, Trash2, Lock, LockOpen } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CAT_EVENT_TYPES = [
  'Hurricane/Tropical Storm', 'Tornado', 'Hail', 'Wind/Windstorm', 'Flood',
  'Wildfire', 'Earthquake', 'Winter Storm/Freeze', 'Severe Convective Storm', 'Other'
];

const EMPTY_FORM = { cat_code: '', event_date: '', event_type: '', description: '', status: 'Open' };

const STATUS_BADGE = {
  Open: 'bg-success-bg text-success-text',
  Closed: 'bg-gray-200 text-gray-700'
};

const formatCurrency = (value) => `$${(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

export const CatEventsPage = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('code-asc');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [deleteEvent, setDeleteEvent] = useState(null);

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/cat-events`);
      setEvents(response.data);
    } catch (error) {
      toast.error('Failed to load CAT events');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const openCreateDialog = async () => {
    setEditingEvent(null);
    let suggestedCode = '';
    try {
      const response = await axios.get(`${API}/cat-events/next-code`);
      suggestedCode = response.data.next_code;
    } catch (error) {
      suggestedCode = '';
    }
    setFormData({ ...EMPTY_FORM, cat_code: suggestedCode });
    setIsDialogOpen(true);
  };

  const openEditDialog = (event) => {
    setEditingEvent(event);
    setFormData({
      cat_code: event.cat_code,
      event_date: event.event_date,
      event_type: event.event_type,
      description: event.description || '',
      status: event.status
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.cat_code.trim()) {
      toast.error('CAT code is required');
      return;
    }
    if (!formData.event_date) {
      toast.error('Event date is required');
      return;
    }
    if (!formData.event_type) {
      toast.error('Event type is required');
      return;
    }

    try {
      if (editingEvent) {
        await axios.put(`${API}/cat-events/${editingEvent.cat_event_id}`, formData);
        toast.success('CAT event updated successfully');
      } else {
        await axios.post(`${API}/cat-events`, formData);
        toast.success('CAT event created successfully');
      }
      setIsDialogOpen(false);
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save CAT event');
    }
  };

  const handleToggleStatus = async (event) => {
    const newStatus = event.status === 'Open' ? 'Closed' : 'Open';
    try {
      await axios.put(`${API}/cat-events/${event.cat_event_id}`, { status: newStatus });
      toast.success(`CAT event marked as ${newStatus}`);
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const confirmDelete = async () => {
    if (!deleteEvent) return;
    try {
      await axios.delete(`${API}/cat-events/${deleteEvent.cat_event_id}`);
      toast.success('CAT event deleted successfully');
      setDeleteEvent(null);
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete CAT event');
      setDeleteEvent(null);
    }
  };

  const handleSort = (column) => {
    setSortBy(prev => {
      if (prev === `${column}-asc`) return `${column}-desc`;
      return `${column}-asc`;
    });
  };

  const getFilteredEvents = () => {
    const term = search.trim().toLowerCase();
    const filtered = events.filter(event =>
      !term ||
      event.cat_code.toLowerCase().includes(term) ||
      event.event_type.toLowerCase().includes(term)
    );

    const [column, direction] = sortBy.split('-');
    const key = column === 'code' ? 'cat_code' : 'event_type';
    filtered.sort((a, b) => {
      const cmp = (a[key] || '').localeCompare(b[key] || '');
      return direction === 'asc' ? cmp : -cmp;
    });
    return filtered;
  };

  const sortIndicator = (column) => {
    if (sortBy === `${column}-asc`) return <span className="text-accent">↑</span>;
    if (sortBy === `${column}-desc`) return <span className="text-accent">↓</span>;
    return <span className="text-slate-300">↕</span>;
  };

  const filteredEvents = getFilteredEvents();

  return (
    <DashboardLayout>
      <div className="p-8" data-testid="cat-events-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold font-sans text-foreground tracking-tight">Catastrophe Events</h1>
            <p className="text-foreground-muted mt-2">Manage CAT events and track assigned claims</p>
          </div>
          <Button
            className="bg-accent hover:bg-accent-hover text-accent-foreground"
            onClick={openCreateDialog}
            data-testid="create-cat-event-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create CAT Event
          </Button>
        </div>

        {/* Search */}
        <div className="mb-6 flex items-center gap-2 max-w-md">
          <Search className="w-5 h-5 text-foreground-muted" />
          <Input
            placeholder="Search by CAT code or event type..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="cat-event-search"
          />
        </div>

        {/* Table */}
        <Card className="border border-border shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12" data-testid="loading-spinner">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
            </div>
          ) : filteredEvents.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="cat-events-table">
                <thead className="bg-slate-50 text-slate-500 font-medium uppercase tracking-wider text-xs border-b border-border">
                  <tr>
                    <th
                      className="px-6 py-4 text-left cursor-pointer hover:bg-slate-100 select-none"
                      onClick={() => handleSort('code')}
                      data-testid="sort-by-code"
                    >
                      <div className="flex items-center gap-1">CAT Code {sortIndicator('code')}</div>
                    </th>
                    <th className="px-6 py-4 text-left">Event Date</th>
                    <th
                      className="px-6 py-4 text-left cursor-pointer hover:bg-slate-100 select-none"
                      onClick={() => handleSort('type')}
                      data-testid="sort-by-type"
                    >
                      <div className="flex items-center gap-1">Event Type {sortIndicator('type')}</div>
                    </th>
                    <th className="px-6 py-4 text-left">Status</th>
                    <th className="px-6 py-4 text-right"># Claims</th>
                    <th className="px-6 py-4 text-right">Total Incurred</th>
                    <th className="px-6 py-4 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredEvents.map((event) => (
                    <tr
                      key={event.cat_event_id}
                      className="hover:bg-slate-50/50 transition-colors"
                      data-testid={`cat-event-row-${event.cat_code}`}
                    >
                      <td className="px-6 py-4 font-mono text-xs font-medium text-foreground">
                        {event.cat_code}
                      </td>
                      <td className="px-6 py-4 text-foreground">{event.event_date}</td>
                      <td className="px-6 py-4 text-foreground">{event.event_type}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_BADGE[event.status] || 'bg-gray-100 text-gray-700'}`}>
                          {event.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right font-mono text-xs text-foreground">
                        {event.claim_count}
                      </td>
                      <td className="px-6 py-4 text-right font-mono text-xs text-foreground">
                        {formatCurrency(event.total_settlement)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/cat-events/${event.cat_event_id}`)}
                            data-testid={`view-cat-event-${event.cat_code}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(event)}
                            data-testid={`edit-cat-event-${event.cat_code}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleStatus(event)}
                            className={event.status === 'Open' ? 'text-amber-600 hover:text-amber-700' : 'text-green-600 hover:text-green-700'}
                            title={event.status === 'Open' ? 'Close event' : 'Reopen event'}
                            data-testid={`toggle-cat-event-${event.cat_code}`}
                          >
                            {event.status === 'Open' ? <Lock className="w-4 h-4" /> : <LockOpen className="w-4 h-4" />}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteEvent(event)}
                            className="text-red-500 hover:text-red-700"
                            data-testid={`delete-cat-event-${event.cat_code}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12" data-testid="no-cat-events">
              <p className="text-foreground-muted">{search ? 'No CAT events match your search' : 'No CAT events yet'}</p>
              {!search && (
                <Button className="mt-4" variant="outline" onClick={openCreateDialog}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First CAT Event
                </Button>
              )}
            </div>
          )}
        </Card>

        {/* Create/Edit Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-2xl font-sans">
                {editingEvent ? 'Edit CAT Event' : 'Create CAT Event'}
              </DialogTitle>
              <DialogDescription>
                {editingEvent ? 'Update the catastrophe event details below.' : 'Enter the catastrophe event details. CAT code, event date and event type are required.'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="cat-event-form">
              <div className="space-y-2">
                <Label htmlFor="cat_code">CAT Code <span className="text-red-500">*</span></Label>
                <Input
                  id="cat_code"
                  value={formData.cat_code}
                  onChange={(e) => setFormData({ ...formData, cat_code: e.target.value })}
                  placeholder="e.g. CAT-2026-001"
                  required
                  data-testid="cat-code-input"
                />
                {!editingEvent && (
                  <p className="text-xs text-foreground-muted">Suggested next code is pre-filled — you can override it.</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="event_date">Event Date <span className="text-red-500">*</span></Label>
                <Input
                  id="event_date"
                  type="date"
                  value={formData.event_date}
                  onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                  required
                  data-testid="cat-event-date-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Event Type <span className="text-red-500">*</span></Label>
                <Select
                  value={formData.event_type}
                  onValueChange={(value) => setFormData({ ...formData, event_type: value })}
                >
                  <SelectTrigger className="bg-white" data-testid="cat-event-type-select">
                    <SelectValue placeholder="Select event type" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {CAT_EVENT_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional description..."
                  rows={3}
                  data-testid="cat-event-description-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger className="bg-white" data-testid="cat-event-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="Open">Open</SelectItem>
                    <SelectItem value="Closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-end gap-4">
                <Button type="button" variant="secondary" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-primary hover:bg-primary-hover" data-testid="submit-cat-event">
                  {editingEvent ? 'Save Changes' : 'Create CAT Event'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteEvent} onOpenChange={(open) => !open && setDeleteEvent(null)}>
          <AlertDialogContent className="bg-white">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete CAT Event</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete {deleteEvent?.cat_code}? Events with assigned claims cannot be deleted — you will be prompted to reassign the claims or close the event instead.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDelete}
                className="bg-red-500 hover:bg-red-600"
                data-testid="confirm-delete-cat-event"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};
